import { clsx } from "clsx";
import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAuthStore } from "@/store/auth";

interface UserAvatarProps {
  fullName: string;
  profilePictureUrl?: string | null;
  /** when provided, adds red outline if this matches the logged-in user's id */
  userId?: string;
  /** xs=w-5, sm=w-6, md=w-8, lg=w-10, xl=w-14 */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  /** adds border border-white for stacked avatar groups */
  border?: boolean;
  /** override initials fallback classes (bg + text color) */
  initialsClassName?: string;
  className?: string;
}

const SIZE: Record<string, string> = {
  xs: "w-5 h-5 text-[10px]",
  sm: "w-6 h-6 text-xs",
  md: "w-8 h-8 text-sm",
  lg: "w-10 h-10 text-sm",
  xl: "w-14 h-14 text-base",
};

export function UserAvatar({
  fullName,
  profilePictureUrl,
  userId,
  size = "md",
  border = false,
  initialsClassName = "bg-[var(--color-primary-dim)] text-[var(--color-primary)] font-bold",
  className,
}: UserAvatarProps) {
  const currentUserId = useAuthStore((s) => s.user?.id);
  const isCurrentUser = !!userId && userId === currentUserId;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);

  const showTooltip = () => {
    timerRef.current = setTimeout(() => {
      if (wrapperRef.current) {
        const r = wrapperRef.current.getBoundingClientRect();
        setTooltipPos({
          top: r.bottom + window.scrollY + 6,
          left: r.left + window.scrollX + r.width / 2,
        });
      }
    }, 500);
  };

  const hideTooltip = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setTooltipPos(null);
  };

  const base = clsx(
    SIZE[size],
    "flex-shrink-0",
    border && "border border-white",
    isCurrentUser && "ring-2 ring-red-500 ring-offset-1",
    className,
  );

  const inner = profilePictureUrl ? (
    <img
      src={profilePictureUrl}
      alt={fullName}
      className={clsx(base, "object-cover")}
    />
  ) : (
    <div
      className={clsx(base, "flex items-center justify-center font-bold", initialsClassName)}
    >
      {fullName.charAt(0).toUpperCase()}
    </div>
  );

  return (
    <>
      <span
        ref={wrapperRef}
        className="inline-flex cursor-default"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
      >
        {inner}
      </span>

      {tooltipPos && createPortal(
        <div
          className="pointer-events-none fixed z-[9999] -translate-x-1/2 flex flex-col items-center"
          style={{ top: tooltipPos.top, left: tooltipPos.left }}
        >
          {/* arrow pointing up */}
          <div className="w-0 h-0 border-l-[6px] border-r-[6px] border-b-[6px] border-l-transparent border-r-transparent border-b-white dark:border-b-gray-800" />
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-1.5 flex flex-col items-center gap-1.5">
            {profilePictureUrl ? (
              <img
                src={profilePictureUrl}
                alt={fullName}
                className="w-20 h-20 object-cover rounded-lg"
              />
            ) : (
              <div
                className={clsx(
                  "w-20 h-20 rounded-lg flex items-center justify-center text-3xl font-bold",
                  initialsClassName,
                )}
              >
                {fullName.charAt(0).toUpperCase()}
              </div>
            )}
            <span className="text-xs font-medium text-gray-700 dark:text-gray-200 max-w-[9rem] text-center leading-tight px-1">
              {fullName}
            </span>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
